// Updated ProfilePage component - OPTIMIZED VERSION (No re-fetching on navigation)
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Camera } from "lucide-react";
import { ReactComponent as MobileNoIcon } from "../../assects/svg/profile/Mobile_no.svg";
import { ReactComponent as EmailIcon } from "../../assects/svg/profile/Email.svg";
import { ReactComponent as JoiningDateIcon } from "../../assects/svg/profile/Joining_date.svg";
import { ReactComponent as BirthdayIcon } from "../../assects/svg/profile/Birthday.svg";
import { ReactComponent as LocationIcon } from "../../assects/svg/profile/Location.svg";
import { ReactComponent as JobTypeIcon } from "../../assects/svg/profile/Job_type.svg";
import { ReactComponent as WorkExperienceIcon } from "../../assects/svg/profile/Work_experience.svg";
import { ReactComponent as SpecializationIcon } from "../../assects/svg/profile/Specialization.svg";
import { ReactComponent as ExpertiseIcon } from "../../assects/svg/profile/Expertise.svg";
import { ReactComponent as LanguageIcon } from "../../assects/svg/profile/Language.svg";
import { useAuth } from "../../context/auth/AuthContext";
import { BASE_URL } from "../../Component/api/url";
import { updateProfilePicture } from "../../services/profile";
import PageHeader from "../Ui_Components/PageHeader";

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user, refreshProfile, isLoadingProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  // Use user data directly from AuthContext - no separate API call needed!
  const profileData = user;

  const handleLogout = () => {
    logout();
    navigate("/signin", { replace: true });
  };

  const logoutButton = (
    <button
      onClick={handleLogout}
      className="hidden sm:flex items-center gap-2 px-4 py-2 text-[#00A9A9] border border-[#00A9A9] rounded-md transition-colors"
    >
      <LogOut className="w-4 h-4" />
      <span className="text-sm font-medium">Logout</span>
    </button>
  );

  const getUserInitials = (): string => {
    if (!profileData) return 'U';
    const firstName = profileData.nutrist_fname || '';
    const lastName = profileData.nutrist_lname || '';
    const firstInitial = firstName.charAt(0).toUpperCase();
    const lastInitial = lastName.charAt(0).toUpperCase();
    return firstInitial && lastInitial ? `${firstInitial}${lastInitial}` : firstInitial || lastInitial || 'U';
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setUploadError(null);

    if (!file) return;

    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file format. Please upload PNG or JPG only.');
      event.target.value = '';
      return;
    }

    // Validate file size (800 KB)
    const maxSize = 800 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size exceeds 800 KB. Please upload a smaller image.');
      event.target.value = '';
      return;
    }

    if (!user?.nutrist_id) {
      setUploadError('Unable to upload image. Please login again.');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);

      const data = await updateProfilePicture(file);

      if (data.success === true || data.success === 1 || data.success === "1") {
        // Refresh profile data from AuthContext
        await refreshProfile();
      } else {
        setUploadError(data.message || 'Failed to upload image. Please try again.');
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setUploadError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleCameraClick = () => {
    setUploadError(null);
    fileInputRef.current?.click();
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return "N/A";
    let date: Date;
    if (dateString.includes('/')) {
      const [dd, mm, yyyy] = dateString.split('/');
      date = new Date(`${yyyy}-${mm}-${dd}`);
    } else {
      date = new Date(dateString);
    }
    if (isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getLanguages = (): string => {
    if (!profileData) return "N/A";
    const languages = [
      profileData.nutrist_lang1,
      profileData.nutrist_lang2,
      profileData.nutrist_lang3,
      profileData.nutrist_lang4
    ].filter(lang => lang && lang.trim() !== "");
    return languages.length > 0 ? languages.join(", ") : "N/A";
  };

  // ===========================================================================
  // OLD RESPONSE — comment this block + uncomment NEW block to switch
  // specialisation_details shape: { spec_id, specialisation, experience }
  // const getSpecializations = (): string => {
  //   if (!profileData?.specialisation_details || profileData.specialisation_details.length === 0) {
  //     return "N/A";
  //   }
  //   return profileData.specialisation_details
  //     .map(spec => `${spec.specialisation} (${spec.experience})`)
  //     .join(", ");
  // };
  //
  // OLD RESPONSE — expertise_details shape: { expertise_id, expertise }
  // const getExpertise = (): string => {
  //   if (!profileData?.expertise_details || profileData.expertise_details.length === 0) {
  //     return "N/A";
  //   }
  //   return profileData.expertise_details
  //     .map(exp => exp.expertise)
  //     .join(", ");
  // };
  // ===========================================================================

  // NEW RESPONSE — comment this block + uncomment OLD block to revert
  // specialisation_details shape: { spec_exp, specialisation }
  const getSpecializations = (): string => {
    if (!profileData?.specialisation_details || profileData.specialisation_details.length === 0) {
      return "N/A";
    }
    return profileData.specialisation_details
      .filter(spec => spec.specialisation && spec.specialisation.trim() !== "")
      .map(spec => spec.spec_exp ? `${spec.specialisation} (${spec.spec_exp})` : spec.specialisation)
      .join(", ") || "N/A";
  };

  // NEW RESPONSE — expertise_details shape: { expertise }
  const getExpertise = (): string => {
    if (!profileData?.expertise_details || profileData.expertise_details.length === 0) {
      return "N/A";
    }
    return profileData.expertise_details
      .filter(exp => exp.expertise && exp.expertise.trim() !== "")
      .map(exp => exp.expertise)
      .join(", ") || "N/A";
  };
  // ===========================================================================

  const getLocation = (): string => {
    if (!profileData) return "N/A";
    const parts = [
      profileData.nutrist_city,
      profileData.nutrist_state,
      "India"
    ].filter(part => part && part.trim() !== "");
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const getFullName = (): string => {
    if (!profileData) return "Loading...";
    const title = profileData.nutrist_title ? `${profileData.nutrist_title} ` : "";
    return `${title}${profileData.nutrist_fname} ${profileData.nutrist_lname}`;
  };

  const getProfileImage = (): string | null => {
    if (profileData?.nutrist_profile) {
      return `${BASE_URL}${profileData.nutrist_profile}`;
    }
    return null;
  };

  // Loading state - only shows on initial app load
  if (isLoadingProfile && !profileData) {
    return (
      <div className="flex-1 bg-[#F7F7FA] min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-base text-gray-500">Loading profile data...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!profileData) {
    return (
      <div className="flex-1 bg-[#F7F7FA] min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load profile</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-[#00A9A9] text-white rounded-lg hover:bg-[#008888] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#F7F7FA] min-h-screen">
      <PageHeader 
        title="Profile" 
        rightContent={logoutButton}
      />

      <div className="px-4 sm:px-6 lg:px-8 pb-8">
        {/* Profile Header Card */}
        <div className="bg-white rounded-[6px] shadow-sm border-[0.5px] border-[#F3F4F6] p-6 mb-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              {getProfileImage() ? (
                <img
                  src={getProfileImage()!}
                  alt="Profile"
                  className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#DAF9F9] flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl font-semibold text-[#00A9A9]">
                    {getUserInitials()}
                  </span>
                </div>
              )}
              <button
                onClick={handleCameraClick}
                disabled={uploading}
                className="absolute bottom-0 right-0 w-5 h-5 bg-white rounded-full border-2 border-white shadow-md flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title={uploading ? "Uploading..." : "Change profile picture"}
              >
                {uploading ? (
                  <div className="w-3 h-3 border-2 border-[#00A9A9] border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Camera className="w-3 h-3 text-[#00A9A9]" />
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-xl sm:text-2xl font-semibold font-inter text-[#111C2D]">
                  {getFullName()}
                </h2>
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#DAF9F9] border-[0.5px] border-[#0DB5B5] text-[#0DB5B5] capitalize">
                  {profileData.nutrist_status}
                </span>
              </div>
              <p className="text-[16px] text-[#111C2D99] font-medium">
                {profileData.nutrist_assign_role}, TRV/N/ID{String(profileData.nutrist_id).padStart(3, '0')}
              </p>
            </div>
          </div>
          {uploadError && (
            <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
              {uploadError}
            </div>
          )}
        </div>

        {/* Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Personal Details Card */}
          <div className="bg-white rounded-[6px] shadow-sm border-[0.5px] border-[#F3F4F6] p-6 lg:col-span-1">
            <h3 className="text-lg font-semibold text-[#1F1F1F] font-inter mb-6">
              Personal Details
            </h3>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <MobileNoIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-medium font-inter">Mobile no.</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    +91 {profileData.nutrist_mobileno}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <EmailIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-medium font-inter">Email</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {profileData.nutrist_email}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <JoiningDateIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Joining date</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {formatDate(profileData.nutrist_joining_date || '')}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <BirthdayIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Birthday</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {formatDate(profileData.nutrist_dob || '')}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <LocationIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Location</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {getLocation()}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Other Details Card */}
          <div className="bg-white rounded-[6px] shadow-sm border-[0.5px] border-[#F3F4F6] p-6 lg:col-span-2">
            <h3 className="text-lg font-semibold text-[#1F1F1F] font-inter mb-6">Other</h3>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <JobTypeIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Job type</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium capitalize">
                    {profileData.nutrist_job_type?.replace('-', ' ') || 'N/A'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <WorkExperienceIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Work experience</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {profileData.nutrist_workexperience || 'N/A'} years
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <SpecializationIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Specialization</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {getSpecializations()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <ExpertiseIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Expertise</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {getExpertise()}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <LanguageIcon className="w-10 h-10" />
                <div>
                  <div className="text-sm text-[#646464] font-inter font-medium">Language</div>
                  <div className="text-sm text-[#1F1F1F] font-inter font-medium">
                    {getLanguages()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;